import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { pollJobUntilDone } from "@/lib/pollJob";
import { ZohoPushStatusBadge } from "@/components/ZohoPushStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import BookVehicleDialog, { BookVehicleButton } from "../components/BookVehicleDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import PermButton from "@/components/PermButton";
import StockItemSearch from "@/components/StockItemSearch";
import { toast } from "sonner";
import {
  Plus, Loader2, FileText, Trash2, Pencil, ExternalLink, X,
  CheckCircle, ArrowRight, RefreshCw,
} from "lucide-react";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- shared visual maps ---------------------------------------------------

const DC_STATUS_COLORS = {
  playfield_draft: "bg-zinc-100 text-zinc-900 border-zinc-300",
  pending_zoho: "bg-blue-100 text-blue-900 border-blue-300",
  push_failed: "bg-rose-100 text-rose-900 border-rose-300",
  rejected: "bg-rose-100 text-rose-900 border-rose-300",
  draft: "bg-amber-100 text-amber-900 border-amber-300",
  open: "bg-emerald-100 text-emerald-900 border-emerald-300",
  delivered: "bg-emerald-100 text-emerald-900 border-emerald-300",
  invoiced: "bg-emerald-100 text-emerald-900 border-emerald-300",
  void: "bg-rose-100 text-rose-900 border-rose-300",
  cancelled: "bg-rose-100 text-rose-900 border-rose-300",
};

const SOURCE_LABEL = {
  STANDALONE: { label: "Standalone", cls: "bg-violet-100 text-violet-900 border-violet-300" },
  IBT: { label: "IBT-linked", cls: "bg-sky-100 text-sky-900 border-sky-300" },
  JOB_WORK: { label: "Job Work", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
};

// A row's display "source" is derived: STANDALONE + challan_type=job_work
// surfaces as JOB_WORK so the badge/filter treat it as its own bucket.
const displaySource = (row) =>
  row?.source === "STANDALONE" && row?.challan_type === "job_work"
    ? "JOB_WORK"
    : row?.source;

// IBT statuses considered "open" for DC creation. A DC is needed once stock
// is actually moving — INITIATED items haven't been dispatched, but the user
// flow has always allowed creating the DC ahead of dispatch.
const IBT_OPEN_STATUSES = new Set([
  "INITIATED",
  "APPROVED",
  "READY_FOR_DISPATCH",
  "IN_TRANSIT",
]);

const blankLine = () => ({ item_id: "", quantity: "", rate: "", item_name: "", item_stock: null, ownership: "BIDSO_SUPPLIED" });

const blankForm = () => ({
  customer_id: "",
  customer_name: "",
  item_type: "RM",
  source_branch: "",
  date: new Date().toISOString().slice(0, 10),
  reference_number: "",
  notes: "",
  consignee_name: "",
  consignee_address: "",
  delivery_method: "",
  // Job Work Lifecycle Phase 1 (Jul 2026). Default flipped to `job_work`
  // per Feb 2026 UX request — most standalone DCs on this org are job-work.
  challan_type: "job_work",   // "others" | "job_work"
  vendor_id: "",
  vendor_name: "",
});

const DeliveryChallan = () => {
  // -------- tabs --------
  const [tab, setTab] = useState("all");

  // -------- All DCs --------
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filterSource, setFilterSource] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPushStatus, setFilterPushStatus] = useState("ALL");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // -------- IBT Pending tab --------
  const [ibtList, setIbtList] = useState([]);
  const [ibtLoading, setIbtLoading] = useState(false);
  const [ibtFilterStatus, setIbtFilterStatus] = useState("ALL");
  const [ibtFilterSrc, setIbtFilterSrc] = useState("ALL");
  const [ibtFilterDst, setIbtFilterDst] = useState("ALL");
  const [ibtFilterFrom, setIbtFilterFrom] = useState("");
  const [ibtFilterTo, setIbtFilterTo] = useState("");

  // -------- Branches (used for filter dropdowns + source-branch picker) ----
  const [branches, setBranches] = useState([]);

  // -------- Job Work vendors (Phase 1) — lazy-loaded when standalone
  //          dialog's challan_type flips to job_work. Backend filters to
  //          is_job_worker=true docs, so no client-side re-filter needed.
  const [jobWorkVendors, setJobWorkVendors] = useState([]);
  const [jwVendorsLoading, setJwVendorsLoading] = useState(false);

  // -------- Standalone create/edit dialog --------
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [rows, setRows] = useState([blankLine()]);
  const [editingId, setEditingId] = useState(null);

  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [searchingCust, setSearchingCust] = useState(false);

  // -------- IBT DC dialog --------
  const [showIbtDC, setShowIbtDC] = useState(false);
  const [ibtDcMode, setIbtDcMode] = useState("create"); // "create" | "edit" | "view"
  const [ibtDcTransfer, setIbtDcTransfer] = useState(null);
  const [ibtDcSubmitting, setIbtDcSubmitting] = useState(false);
  const [ibtDcItemBlockers, setIbtDcItemBlockers] = useState(null);
  const [ibtDcForm, setIbtDcForm] = useState({
    customer_id: "",
    customer_name: "",
    challan_type: "others",
    date: new Date().toISOString().slice(0, 10),
    reference_number: "",
    notes: "",
    terms_and_conditions: "",
  });
  const [ibtCustQuery, setIbtCustQuery] = useState("");
  const [ibtCustResults, setIbtCustResults] = useState([]);
  const [ibtCustSearching, setIbtCustSearching] = useState(false);

  // -------- Detail dialog --------
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bookVehicleOpen, setBookVehicleOpen] = useState(false);

  const getHeaders = () => ({
    Authorization: `Bearer ${useAuthStore.getState().token}`,
  });

  // ----- fetchers ---------------------------------------------------------

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filterSource !== "ALL") params.set("source", filterSource);
      if (filterStatus !== "ALL") params.set("status", filterStatus);
      if (filterPushStatus !== "ALL") params.set("push_status", filterPushStatus);
      const res = await axios.get(`${API}/delivery-challans?${params}`, { headers: getHeaders() });
      setList(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Delivery Challans");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterSource, filterStatus, filterPushStatus]);

  const fetchIBTPending = useCallback(async () => {
    setIbtLoading(true);
    try {
      const res = await axios.get(`${API}/ibt-transfers`, { headers: getHeaders() });
      // Show every open IBT (regardless of whether it already has a DC) so
      // the user can see status + edit/view existing DCs inline. CANCELLED /
      // REJECTED transfers are still excluded — they no longer need a DC.
      const open = (res.data || []).filter(
        (t) => t.status !== "CANCELLED" && t.status !== "REJECTED",
      );
      setIbtList(open);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load open IBTs");
    } finally {
      setIbtLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "all") fetchList(); }, [tab, fetchList]);
  useEffect(() => { if (tab === "ibt") fetchIBTPending(); }, [tab, fetchIBTPending]);

  useEffect(() => {
    // Permission-aware: master_admin gets all branches, branch_ops gets only
    // their assigned branches. Endpoint `/api/master-data/branches` doesn't
    // exist (legacy stub) — `/api/branch-ops/my-branches` is the real one.
    axios.get(`${API}/branch-ops/my-branches`, { headers: getHeaders() })
      .then((res) => setBranches(res.data?.branches || []))
      .catch(() => setBranches([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced customer search — used by both dialogs (separate state for each).
  useEffect(() => {
    if (!showCreate || !custQuery || custQuery.length < 2) {
      setCustResults([]);
      // Feb 2026 — always clear the loading flag on early return so the
      // visible min-char hint isn't hidden after backspacing from a
      // longer query to a sub-minimum one.
      setSearchingCust(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearchingCust(true);
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(custQuery)}&limit=10`,
          { headers: getHeaders() }
        );
        if (!cancelled) setCustResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setCustResults([]);
      } finally {
        if (!cancelled) setSearchingCust(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [custQuery, showCreate]);

  useEffect(() => {
    if (!showIbtDC || !ibtCustQuery || ibtCustQuery.length < 2) {
      setIbtCustResults([]);
      // Feb 2026 — see comment above.
      setIbtCustSearching(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setIbtCustSearching(true);
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(ibtCustQuery)}&limit=10`,
          { headers: getHeaders() }
        );
        if (!cancelled) setIbtCustResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setIbtCustResults([]);
      } finally {
        if (!cancelled) setIbtCustSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ibtCustQuery, showIbtDC]);

  // Lazy-load job worker vendors on demand — only when user flips the
  // challan_type dropdown to `job_work`. Cached for the session so
  // subsequent opens don't re-hit the API.
  useEffect(() => {
    if (form.challan_type !== "job_work") return;
    if (jobWorkVendors.length > 0 || jwVendorsLoading) return;
    let cancelled = false;
    (async () => {
      setJwVendorsLoading(true);
      try {
        const res = await axios.get(`${API}/vendors?is_job_worker=true`, { headers: getHeaders() });
        if (!cancelled) setJobWorkVendors(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        if (!cancelled) toast.error("Failed to load job-work vendors");
      } finally {
        if (!cancelled) setJwVendorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.challan_type]);

  // ----- client-side filters for All DCs ----------------------------------
  // Customer/date filters are applied client-side because the list endpoint
  // returns a unified page already; doing it here keeps the backend lean.

  const filteredList = useMemo(() => {
    const cust = filterCustomer.trim().toLowerCase();
    return list.filter((r) => {
      if (cust && !(r.customer_name || "").toLowerCase().includes(cust)) return false;
      if (filterDateFrom && (r.date || "") < filterDateFrom) return false;
      if (filterDateTo && (r.date || "") > filterDateTo) return false;
      return true;
    });
  }, [list, filterCustomer, filterDateFrom, filterDateTo]);

  // ----- IBT subtab client-side filters -----------------------------------

  const filteredIBT = useMemo(() => {
    return ibtList.filter((t) => {
      if (ibtFilterStatus !== "ALL" && t.status !== ibtFilterStatus) return false;
      if (ibtFilterSrc !== "ALL" && t.source_branch !== ibtFilterSrc) return false;
      if (ibtFilterDst !== "ALL" && t.destination_branch !== ibtFilterDst) return false;
      const d = (t.initiated_at || "").slice(0, 10);
      if (ibtFilterFrom && d < ibtFilterFrom) return false;
      if (ibtFilterTo && d > ibtFilterTo) return false;
      return true;
    });
  }, [ibtList, ibtFilterStatus, ibtFilterSrc, ibtFilterDst, ibtFilterFrom, ibtFilterTo]);

  // ----- standalone dialog handlers ---------------------------------------

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setRows([blankLine()]);
    setCustQuery("");
    setCustResults([]);
    setShowCreate(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      customer_id: row.customer_zoho_id || "",
      customer_name: row.customer_name || "",
      item_type: row.item_type || "RM",
      source_branch: row.source_branch || "",
      date: row.date || new Date().toISOString().slice(0, 10),
      reference_number: row.reference_number || "",
      notes: row.notes || "",
      consignee_name: row.consignee_name || "",
      consignee_address: row.consignee_address || "",
      delivery_method: row.delivery_method || "",
      challan_type: row.challan_type || "others",
      vendor_id: row.vendor_id || "",
      vendor_name: row.vendor_name || "",
    });
    setRows((row.line_items || []).map((li) => ({
      item_id: li.item_id || "",
      quantity: String(li.quantity ?? ""),
      rate: String(li.rate ?? ""),
      item_name: li.name || "",
      item_stock: null,
      ownership: li.ownership || "BIDSO_SUPPLIED",
    })));
    setShowCreate(true);
  };

  // ─── Save-vs-Push decoupling (Feb 2026) ───
  // Retry the Zoho push for a DC that failed / never pushed. Uses the
  // save-vs-push separation the backend gained in Feb 2026 — this endpoint
  // never touches stock on any path, so retrying N times has zero
  // inventory side effects. Refreshes the list on completion.
  const [pushingIds, setPushingIds] = useState(new Set());
  const [deletingIds, setDeletingIds] = useState(new Set());

  const retryPush = async (row) => {
    if (!row?.id || row.source !== "STANDALONE") return;
    if (pushingIds.has(row.id)) return;
    setPushingIds((prev) => new Set(prev).add(row.id));
    try {
      const res = await axios.post(
        `${API}/delivery-challans/${row.id}/push-to-zoho`,
        {},
        { headers: getHeaders() },
      );
      // pending → poll the job. Otherwise, the response is the DC dict.
      if (res.data?.status === "pending" && res.data?.job_id) {
        toast.info(`Push queued (${row.dc_code}) — tracking…`);
        try {
          await pollJobUntilDone(res.data.job_id, { headers: getHeaders() });
          toast.success(`DC ${row.dc_code} pushed to Zoho.`);
        } catch (jobErr) {
          toast.error(
            `Push job for ${row.dc_code} failed: ${jobErr?.message || "unknown"}`,
          );
        }
      } else {
        toast.success(res.data?.message || `DC ${row.dc_code} pushed to Zoho.`);
      }
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || err?.message || "Push failed");
      toast.error(`Push failed: ${msg}`);
    } finally {
      setPushingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const releaseDc = async (row) => {
    if (!row?.id || row.source !== "STANDALONE") return;
    if (deletingIds.has(row.id)) return;
    const stateLabel = row.zoho_dc_status || "draft";
    const ok = window.confirm(
      `Release DC ${row.dc_code}?\n\n` +
      `Current status: ${stateLabel}\n` +
      `This restores the source-branch stock and removes the DC from Playfield. ` +
      `The Zoho document (if one exists) is NOT voided — void it in Zoho separately.`,
    );
    if (!ok) return;
    setDeletingIds((prev) => new Set(prev).add(row.id));
    try {
      await axios.delete(
        `${API}/delivery-challans/${row.id}`,
        { headers: getHeaders() },
      );
      toast.success(`DC ${row.dc_code} released. Stock restored.`);
      await fetchList();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || err?.message || "Release failed");
      toast.error(`Release failed: ${msg}`);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  // Which statuses expose the Retry Push and Release actions on a row.
  // Aligned with the backend `push-to-zoho` allow-list and the DELETE
  // allow-list respectively.
  const RETRY_PUSH_STATES = new Set([
    "playfield_draft", "push_failed", "pending_zoho", "rejected",
  ]);
  const RELEASE_STATES = new Set([
    "playfield_draft", "push_failed", "pending_zoho", "rejected", "draft",
  ]);

  const openDetail = async (row) => {
    if (row.source === "IBT") {
      // IBT-linked DCs don't have a `delivery_challans` document; open the
      // IBT DC dialog in view mode using the live IBT row instead.
      const t = ibtList.find((x) => x.id === row.linked_ibt_id);
      if (t) {
        openIbtDCEdit(t, "view");
        return;
      }
      try {
        const res = await axios.get(`${API}/ibt-transfers/${row.linked_ibt_id}`, { headers: getHeaders() });
        openIbtDCEdit(res.data, "view");
      } catch {
        toast.error("Failed to load IBT for DC view");
      }
      return;
    }
    try {
      const res = await axios.get(`${API}/delivery-challans/${row.id}`, { headers: getHeaders() });
      setDetail(res.data);
      setShowDetail(true);
    } catch {
      toast.error("Failed to load DC");
    }
  };

  const setRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, blankLine()]);
  const removeRow = (idx) => setRows((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs));

  // Only Zoho-required fields are enforced now. For challan_type=="job_work"
  // the customer is derived server-side from the vendor, so the caller only
  // needs to pick a vendor. For "others" the standalone contract remains
  // unchanged (customer required).
  const validateStandalone = () => {
    if (form.challan_type === "job_work") {
      if (!form.vendor_id) { toast.error("Job Work: pick a job-worker vendor"); return false; }
    } else {
      if (!form.customer_id) { toast.error("Customer is required"); return false; }
    }
    if (!form.source_branch) { toast.error("Source branch is required — standalone DC debits inventory from it"); return false; }
    if (!form.date) { toast.error("Date is required"); return false; }
    if (!rows.length) { toast.error("At least one line item is required"); return false; }
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.item_id) { toast.error(`Row ${i + 1}: item missing`); return false; }
      if (!(Number(r.quantity) > 0)) { toast.error(`Row ${i + 1}: quantity must be > 0`); return false; }
      if (!(Number(r.rate) >= 0)) { toast.error(`Row ${i + 1}: rate must be >= 0`); return false; }
    }
    return true;
  };

  // `pushAfterSave` (Feb 2026): when true, the frontend chains a
  // POST /delivery-challans/{id}/push-to-zoho immediately after a
  // successful save. When false (default), the DC lands in Playfield
  // as a `playfield_draft` and the user can push later from the row.
  // NOTE: the two paths ALWAYS hit the same save endpoint first — the
  // backend never accepts a combined "save+push" payload. This keeps
  // stock effects deterministic (deducted exactly once at save) and
  // matches the split lifecycle the Dispatch invoice already ships.
  const submitCreate = async ({ pushAfterSave = false } = {}) => {
    if (!validateStandalone()) return;
    setSubmitting(true);
    try {
      const payload = {
        // Job Work DCs derive customer server-side; sending an empty
        // customer_id is fine on `others` (backend 400s if required).
        customer_id: form.challan_type === "job_work" ? undefined : form.customer_id,
        customer_name: form.challan_type === "job_work" ? undefined : form.customer_name,
        item_type: form.item_type,
        source_branch: form.source_branch,
        date: form.date,
        reference_number: form.reference_number || undefined,
        notes: form.notes || undefined,
        consignee_name: form.consignee_name || undefined,
        consignee_address: form.consignee_address || undefined,
        delivery_method: form.delivery_method || undefined,
        challan_type: form.challan_type || "others",
        vendor_id: form.challan_type === "job_work" ? form.vendor_id : undefined,
        line_items: rows.map((r) => ({
          item_id: r.item_id,
          quantity: Number(r.quantity),
          rate: Number(r.rate),
          ownership: r.ownership || "BIDSO_SUPPLIED",
        })),
      };
      const url = editingId
        ? `${API}/delivery-challans/${editingId}`
        : `${API}/delivery-challans`;
      const fn = editingId ? axios.put : axios.post;
      const res = await fn(url, payload, { headers: getHeaders() });

      // Feb 2026: standalone DC create now uses the Zoho push orchestrator.
      // When the remote call exceeds 8s the backend returns
      // `{status:"pending", job_id}`. Close the dialog, refresh the list,
      // and poll up to 60s.
      if (!editingId && res?.data?.status === "pending" && res?.data?.job_id) {
        const jobId = res.data.job_id;
        const dcCode = res.data.dc_code || "Delivery Challan";
        toast.success(
          res.data.message ||
            `${dcCode} is being pushed to Zoho in the background…`,
          { duration: 6000 }
        );
        setShowCreate(false);
        setEditingId(null);
        fetchList();
        try {
          const final = await pollJobUntilDone(jobId);
          if (final.status === "COMPLETED") {
            const r = final.result || {};
            const num = r?.dc?.zoho_dc_number;
            toast.success(num ? `Zoho DC ${num} created` : "DC pushed to Zoho Books");
          } else {
            toast.error(`Zoho DC creation failed: ${final.error || "unknown error"}`, {
              duration: 12000,
            });
          }
        } catch (pollErr) {
          if (pollErr?.reason === "timeout") {
            toast.message(
              `Still pushing ${dcCode} to Zoho — check back in a minute.`,
              { duration: 8000 }
            );
          } else {
            toast.error(`Could not track Zoho push: ${pollErr.message || pollErr}`);
          }
        } finally {
          fetchList();
          setSubmitting(false);
        }
        return;
      }

      const savedDc = res.data?.dc || {};
      const savedDcId = savedDc.id || editingId;
      const savedDcCode = savedDc.dc_code || "Delivery Challan";

      if (!pushAfterSave) {
        toast.success(
          res.data.message ||
            (editingId
              ? `DC ${savedDcCode} updated in Playfield. Push to Zoho when ready.`
              : `DC ${savedDcCode} saved in Playfield. Push to Zoho when ready.`),
        );
        setShowCreate(false);
        setEditingId(null);
        fetchList();
        return;
      }

      // Save & Push flow: keep dialog open with a spinner, chain push,
      // let retryPush's toast + fetchList handle completion feedback.
      toast.success(`DC ${savedDcCode} saved. Pushing to Zoho…`);
      setShowCreate(false);
      setEditingId(null);
      // The backend endpoint accepts any of playfield_draft/push_failed/
      // pending_zoho/rejected — a fresh save is always playfield_draft.
      // retryPush handles both the sync + async (job_id polling) branches.
      await retryPush({ id: savedDcId, dc_code: savedDcCode, source: "STANDALONE" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          (detail && detail.message) ||
          err.message ||
          "DC save failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ----- IBT DC dialog handlers -------------------------------------------

  const openIbtDC = (transfer) => {
    setIbtDcTransfer(transfer);
    setIbtDcMode("create");
    setIbtDcItemBlockers(null);
    setIbtCustQuery("");
    setIbtCustResults([]);
    setIbtDcForm({
      customer_id: "",
      customer_name: "",
      challan_type: "others",
      date: new Date().toISOString().slice(0, 10),
      reference_number: transfer?.transfer_code || "",
      notes: "",
      terms_and_conditions: "",
    });
    setShowIbtDC(true);
  };

  // Pre-fills the dialog from the existing IBT row so the user can edit
  // (or simply review) a DC that's already in Zoho. Pre-existing customer
  // shows as a green chip just like the standalone dialog.
  const openIbtDCEdit = (transfer, mode = "edit") => {
    setIbtDcTransfer(transfer);
    setIbtDcMode(mode);
    setIbtDcItemBlockers(null);
    setIbtCustQuery(transfer.zoho_dc_customer_name || "");
    setIbtCustResults([]);
    setIbtDcForm({
      customer_id: transfer.zoho_dc_customer_id || "",
      customer_name: transfer.zoho_dc_customer_name || "",
      challan_type: transfer.zoho_dc_challan_type || "others",
      date: transfer.zoho_dc_date || new Date().toISOString().slice(0, 10),
      reference_number: transfer.zoho_dc_reference_number || transfer.transfer_code || "",
      notes: transfer.zoho_dc_notes || "",
      terms_and_conditions: transfer.zoho_dc_terms_and_conditions || "",
    });
    setShowIbtDC(true);
  };

  const submitIbtDC = async () => {
    if (!ibtDcTransfer) return;
    if (!ibtDcForm.customer_id) {
      toast.error("Select a customer first");
      return;
    }
    setIbtDcSubmitting(true);
    setIbtDcItemBlockers(null);
    try {
      const payload = {
        customer_id: ibtDcForm.customer_id,
        challan_type: ibtDcForm.challan_type || "others",
        date: ibtDcForm.date,
        reference_number: ibtDcForm.reference_number || undefined,
        notes: ibtDcForm.notes || undefined,
        terms_and_conditions: ibtDcForm.terms_and_conditions || undefined,
      };
      const isEdit = ibtDcMode === "edit";
      const fn = isEdit ? axios.put : axios.post;
      const res = await fn(
        `${API}/ibt-transfers/${ibtDcTransfer.id}/delivery-challan`,
        payload,
        { headers: getHeaders() },
      );

      // Feb 2026: IBT DC create now uses the Zoho push orchestrator. Edit
      // path stays synchronous (smaller payload). Pending → poll up to 60s.
      if (!isEdit && res?.data?.status === "pending" && res?.data?.job_id) {
        const jobId = res.data.job_id;
        const code = ibtDcTransfer?.transfer_code || "DC";
        toast.success(
          res.data.message ||
            `Delivery Challan for ${code} is being pushed to Zoho in the background…`,
          { duration: 6000 }
        );
        setShowIbtDC(false);
        fetchIBTPending();
        fetchList();
        try {
          const final = await pollJobUntilDone(jobId);
          if (final.status === "COMPLETED") {
            const r = final.result || {};
            toast.success(
              r.deliverychallan_number
                ? `Draft DC created in Zoho: ${r.deliverychallan_number}`
                : `DC for ${code} pushed to Zoho`,
              { duration: 6000 }
            );
          } else {
            toast.error(`Zoho DC creation failed: ${final.error || "unknown error"}`, {
              duration: 12000,
            });
          }
        } catch (pollErr) {
          if (pollErr?.reason === "timeout") {
            toast.message(
              `Still pushing DC for ${code} to Zoho — check back in a minute.`,
              { duration: 8000 }
            );
          } else {
            toast.error(`Could not track Zoho push: ${pollErr.message || pollErr}`);
          }
        } finally {
          fetchIBTPending();
          fetchList();
          setIbtDcSubmitting(false);
        }
        return;
      }

      toast.success(
        isEdit
          ? `DC ${res.data.deliverychallan_number || ""} updated in Zoho`
          : `Draft DC created in Zoho: ${res.data.deliverychallan_number}`,
      );
      setShowIbtDC(false);
      fetchIBTPending();
      fetchList();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (detail?.code === "ITEM_MASTER_INCOMPLETE") {
        setIbtDcItemBlockers(detail);
        toast.error(detail.message || "Some items need master data updates before the DC can be created.");
        return;
      }
      let reason = "Unknown error";
      if (typeof detail === "string") reason = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) {
        reason = detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join("; ");
      } else if (detail && typeof detail === "object") {
        reason = detail.message || detail.error || JSON.stringify(detail);
      } else if (err.message) reason = err.message;
      const hint = status === 503
        ? " (Zoho integration not configured.)"
        : status === 502
        ? " (Zoho Books rejected the request.)"
        : status === 409
        ? " (DC has left draft — edits must happen in Zoho.)"
        : "";
      toast.error(`Failed to save DC: ${reason}${hint}`, { duration: 8000 });
    } finally {
      setIbtDcSubmitting(false);
    }
  };

  // ----- render -----------------------------------------------------------

  return (
    <div className="space-y-6" data-testid="dc-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Delivery Challans</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl text-sm">
            All Delivery Challans in one place — standalone and IBT-linked.
            Standalone DCs are created here for shipments that need a Zoho
            challan but aren&apos;t part of an IBT or Dispatch invoice; IBT-linked
            DCs originate from open inter-branch transfers.
          </p>
        </div>
        <PermButton
          entity="Dispatch" action="CREATE"
          onClick={openCreate}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="dc-create-standalone-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Standalone DC
        </PermButton>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="dc-tabs">
          <TabsTrigger value="all" data-testid="dc-tab-all">All DCs</TabsTrigger>
          <TabsTrigger value="ibt" data-testid="dc-tab-ibt">
            Open IBTs
            {ibtList.filter((t) => !t.zoho_dc_id).length > 0 && (
              <span className="ml-2 text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded" title="Open IBTs without a DC">
                {ibtList.filter((t) => !t.zoho_dc_id).length} pending
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ============================= ALL DCs ============================ */}
        <TabsContent value="all" className="space-y-4 mt-4">
          <Card data-testid="dc-filters">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Source</Label>
                  <Select value={filterSource} onValueChange={(v) => { setFilterSource(v); setPage(1); }}>
                    <SelectTrigger data-testid="dc-filter-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All sources</SelectItem>
                      <SelectItem value="STANDALONE">Standalone</SelectItem>
                      <SelectItem value="IBT">IBT-linked</SelectItem>
                      <SelectItem value="JOB_WORK">Job Work</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Status</Label>
                  <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                    <SelectTrigger data-testid="dc-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All status</SelectItem>
                      <SelectItem value="playfield_draft">Playfield draft (not yet pushed)</SelectItem>
                      <SelectItem value="push_failed">Push failed</SelectItem>
                      <SelectItem value="pending_zoho">Pending Zoho (legacy)</SelectItem>
                      <SelectItem value="rejected">Rejected by Zoho</SelectItem>
                      <SelectItem value="draft">Draft (in Zoho)</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="invoiced">Invoiced</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Push status</Label>
                  <Select value={filterPushStatus} onValueChange={(v) => { setFilterPushStatus(v); setPage(1); }}>
                    <SelectTrigger data-testid="dc-filter-push-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All push states</SelectItem>
                      <SelectItem value="NOT_STARTED">Not started</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="PENDING_UPDATE">Pending update</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                      <SelectItem value="FAILED">Failed</SelectItem>
                      <SelectItem value="EXHAUSTED">Exhausted (permanent)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Customer / Destination</Label>
                  <Input
                    placeholder="Search customer / branch"
                    value={filterCustomer}
                    onChange={(e) => setFilterCustomer(e.target.value)}
                    data-testid="dc-filter-customer"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Date From</Label>
                  <Input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    data-testid="dc-filter-date-from"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Date To</Label>
                  <Input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    data-testid="dc-filter-date-to"
                  />
                </div>
              </div>
              <div className="flex justify-between items-center mt-3 text-xs text-zinc-500">
                <span>
                  Showing <strong>{filteredList.length}</strong> of <strong>{total}</strong> DC(s)
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterSource("ALL");
                    setFilterStatus("ALL");
                    setFilterCustomer("");
                    setFilterDateFrom("");
                    setFilterDateTo("");
                    setPage(1);
                  }}
                  data-testid="dc-filter-clear"
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">All DCs ({total})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
              ) : filteredList.length === 0 ? (
                <div className="p-8 text-center text-zinc-500" data-testid="dc-empty">
                  <FileText className="w-8 h-8 mx-auto text-zinc-400" />
                  <div className="mt-2">No DCs match the current filters.</div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">DC Code</th>
                      <th className="px-4 py-3 text-left">Source</th>
                      <th className="px-4 py-3 text-left">Customer / Destination</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Zoho #</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((row) => (
                      <tr key={`${row.source}-${row.id}`} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                        <td className="px-4 py-3 font-mono text-xs">{row.dc_code || "—"}</td>
                        <td className="px-4 py-3">
                          {(() => {
                            const src = displaySource(row);
                            return (
                              <Badge
                                className={`${SOURCE_LABEL[src]?.cls || ""} border text-xs`}
                                data-testid={`dc-row-source-${row.dc_code}`}
                              >
                                {SOURCE_LABEL[src]?.label || src}
                              </Badge>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">{row.customer_name || "—"}</td>
                        <td className="px-4 py-3">{row.date || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{row.zoho_dc_number || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {row.zoho_dc_status && (
                              <Badge className={`${DC_STATUS_COLORS[row.zoho_dc_status] || "bg-zinc-100"} border text-xs`}>
                                {row.zoho_dc_status}
                              </Badge>
                            )}
                            <ZohoPushStatusBadge
                              status={row.zoho_push_status}
                              error={row.zoho_push_error}
                              retryCount={row.zoho_push_retry_count || 0}
                              testIdPrefix={`dc-${row.dc_code}`}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">₹{Number(row.total_amount || 0).toLocaleString("en-IN")}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetail(row)}
                              data-testid={`dc-view-${row.dc_code}`}
                              title="View details"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            {/* Edit while DC is still in draft — applies to both
                                STANDALONE and IBT-linked DCs. Backend 409s once
                                status leaves draft as defence-in-depth. */}
                            {row.zoho_dc_status === "draft" && (
                              <PermButton
                                entity="Dispatch" action="UPDATE"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (row.source === "STANDALONE") {
                                    openEdit(row);
                                  } else {
                                    const t = ibtList.find((x) => x.id === row.linked_ibt_id);
                                    if (t) {
                                      openIbtDCEdit(t, "edit");
                                    } else {
                                      axios.get(`${API}/ibt-transfers/${row.linked_ibt_id}`, { headers: getHeaders() })
                                        .then((res) => openIbtDCEdit(res.data, "edit"))
                                        .catch(() => toast.error("Failed to load IBT for edit"));
                                    }
                                  }
                                }}
                                data-testid={`dc-edit-${row.dc_code}`}
                                title="Edit DC (syncs to Zoho)"
                              >
                                <Pencil className="w-4 h-4" />
                              </PermButton>
                            )}
                            {/* Retry Push — standalone DC only. Visible for
                                any status the backend push allow-list
                                accepts (playfield_draft, push_failed,
                                pending_zoho, rejected). Feb 2026 (spec AC7). */}
                            {row.source === "STANDALONE" && RETRY_PUSH_STATES.has(row.zoho_dc_status) && (
                              <PermButton
                                entity="Dispatch" action="UPDATE"
                                variant="ghost"
                                size="sm"
                                onClick={() => retryPush(row)}
                                disabled={pushingIds.has(row.id)}
                                data-testid={`dc-retry-push-${row.dc_code}`}
                                title="Retry Zoho push (never re-touches stock)"
                              >
                                {pushingIds.has(row.id)
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <RefreshCw className="w-4 h-4" />}
                              </PermButton>
                            )}
                            {/* Release / Delete — standalone DC only.
                                Reverses stock exactly once and removes
                                the local doc. Feb 2026 (spec AC8). */}
                            {row.source === "STANDALONE" && RELEASE_STATES.has(row.zoho_dc_status) && (
                              <PermButton
                                entity="Dispatch" action="DELETE"
                                variant="ghost"
                                size="sm"
                                onClick={() => releaseDc(row)}
                                disabled={deletingIds.has(row.id)}
                                data-testid={`dc-release-${row.dc_code}`}
                                title="Release DC (restore stock + remove)"
                              >
                                {deletingIds.has(row.id)
                                  ? <Loader2 className="w-4 h-4 animate-spin text-rose-600" />
                                  : <Trash2 className="w-4 h-4 text-rose-600" />}
                              </PermButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Pagination — always visible when there's data; mirrors
                  the IBT pattern with First/Prev/Next/Last buttons. */}
              {total > 0 && (
                <div className="flex justify-between items-center px-4 py-3 border-t border-zinc-100 text-sm" data-testid="dc-pagination">
                  <div className="text-zinc-500">
                    Page <strong>{page}</strong> of <strong>{Math.max(1, Math.ceil(total / pageSize))}</strong>
                    {" "}· {pageSize} per page · {total} total
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                      data-testid="dc-pagination-first"
                    >First</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="dc-pagination-prev"
                    >Previous</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page * pageSize >= total}
                      onClick={() => setPage((p) => p + 1)}
                      data-testid="dc-pagination-next"
                    >Next</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page * pageSize >= total}
                      onClick={() => setPage(Math.max(1, Math.ceil(total / pageSize)))}
                      data-testid="dc-pagination-last"
                    >Last</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================ IBT PENDING ========================= */}
        <TabsContent value="ibt" className="space-y-4 mt-4">
          <Card data-testid="dc-ibt-filters">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Status</Label>
                  <Select value={ibtFilterStatus} onValueChange={setIbtFilterStatus}>
                    <SelectTrigger data-testid="dc-ibt-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All statuses</SelectItem>
                      {Array.from(IBT_OPEN_STATUSES).map((s) => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                      ))}
                      <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">From Branch</Label>
                  <Select value={ibtFilterSrc} onValueChange={setIbtFilterSrc}>
                    <SelectTrigger data-testid="dc-ibt-filter-src"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      {branches.map((b) => <SelectItem key={`src-${b}`} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">To Branch</Label>
                  <Select value={ibtFilterDst} onValueChange={setIbtFilterDst}>
                    <SelectTrigger data-testid="dc-ibt-filter-dst"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      {branches.map((b) => <SelectItem key={`dst-${b}`} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Date From</Label>
                  <Input
                    type="date"
                    value={ibtFilterFrom}
                    onChange={(e) => setIbtFilterFrom(e.target.value)}
                    data-testid="dc-ibt-filter-date-from"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-zinc-500">Date To</Label>
                  <Input
                    type="date"
                    value={ibtFilterTo}
                    onChange={(e) => setIbtFilterTo(e.target.value)}
                    data-testid="dc-ibt-filter-date-to"
                  />
                </div>
              </div>
              <div className="flex justify-between items-center mt-3 text-xs text-zinc-500">
                <span>
                  Showing <strong>{filteredIBT.length}</strong> of <strong>{ibtList.length}</strong> open IBT(s)
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIbtFilterStatus("ALL");
                    setIbtFilterSrc("ALL");
                    setIbtFilterDst("ALL");
                    setIbtFilterFrom("");
                    setIbtFilterTo("");
                  }}
                  data-testid="dc-ibt-filter-clear"
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Open IBTs ({filteredIBT.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {ibtLoading ? (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
              ) : filteredIBT.length === 0 ? (
                <div className="p-8 text-center text-zinc-500" data-testid="dc-ibt-empty">
                  <FileText className="w-8 h-8 mx-auto text-zinc-400" />
                  <div className="mt-2">No open IBTs match the current filters.</div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Transfer Code</th>
                      <th className="px-4 py-3 text-left">From → To</th>
                      <th className="px-4 py-3 text-left">Items</th>
                      <th className="px-4 py-3 text-left">IBT Status</th>
                      <th className="px-4 py-3 text-left">DC Status</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIBT.map((t) => {
                      const dcStatus = (t.zoho_dc_status || "").toLowerCase();
                      const hasDC = Boolean(t.zoho_dc_id);
                      return (
                        <tr key={t.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`dc-ibt-row-${t.transfer_code}`}>
                          <td className="px-4 py-3 font-mono text-xs font-bold text-blue-600">{t.transfer_code}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-xs">
                              <span>{t.source_branch}</span>
                              <ArrowRight className="w-3 h-3 text-zinc-400" />
                              <span>{t.destination_branch}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {t.items?.length > 0
                              ? t.items.map((i) => `${i.item_id} × ${i.quantity}`).join(", ")
                              : `${t.item_id} × ${t.quantity}`}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className="bg-zinc-100 text-zinc-700 border text-xs">{(t.status || "").replace("_", " ")}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            {hasDC ? (
                              <div className="flex flex-col gap-0.5">
                                <Badge className={`${DC_STATUS_COLORS[dcStatus] || "bg-zinc-100"} border text-xs w-fit`}>
                                  {dcStatus || "—"}
                                </Badge>
                                {t.zoho_dc_number && (
                                  <span className="font-mono text-[10px] text-zinc-500">{t.zoho_dc_number}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-zinc-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-zinc-500">{t.initiated_at ? new Date(t.initiated_at).toLocaleDateString() : "—"}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {!hasDC && (
                                <PermButton
                                  entity="Dispatch" action="CREATE"
                                  size="sm"
                                  onClick={() => openIbtDC(t)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid={`dc-ibt-create-${t.transfer_code}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-1" />
                                  Create DC
                                </PermButton>
                              )}
                              {hasDC && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openIbtDCEdit(t, "view")}
                                  data-testid={`dc-ibt-view-${t.transfer_code}`}
                                  title="View DC details — edit available in the All DCs tab"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* =================== Standalone Create / Edit dialog ================ */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dc-create-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Standalone DC" : "Create Standalone DC"}</DialogTitle>
            <DialogDescription>
              Saving the DC records it in Playfield and debits source-branch stock — it does <strong>NOT</strong> push to Zoho.
              Use <em>Save &amp; Push to Zoho</em> to do both in one step, or push later from the DC row.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Job Work DC — Yes/No. Fully decoupled from Zoho (Feb 2026):
                Zoho always receives challan_type="others"; this flag drives
                ONLY the local vendor picker + `job_worker_stock` balance
                tracking. The payload key stays `challan_type` (mapping
                Yes→"job_work" / No→"others") so the backend model +
                `standalone_dc_lifecycle.py` reader don't need touching.
                Compact checkbox layout (Feb 2026) — default = Yes. */}
            <div>
              <div className="flex items-center gap-4 flex-wrap" role="radiogroup" aria-label="Job Work DC">
                <Label className="text-sm mb-0">Job Work DC?</Label>
                <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.challan_type === "job_work"}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setForm({
                          ...form,
                          challan_type: "job_work",
                          // Clear customer fields — job-work derives them from the vendor.
                          customer_id: "",
                          customer_name: "",
                        });
                      }
                    }}
                    data-testid="dc-jobwork-yes"
                    aria-label="Job Work DC: Yes"
                  />
                  <span>Yes</span>
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.challan_type !== "job_work"}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setForm({
                          ...form,
                          challan_type: "others",
                          // Clear job-work-only fields on switch to keep the
                          // submit payload clean.
                          vendor_id: "",
                          vendor_name: "",
                        });
                      }
                    }}
                    data-testid="dc-jobwork-no"
                    aria-label="Job Work DC: No"
                  />
                  <span>No</span>
                </label>
              </div>
              {form.challan_type === "job_work" && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Tracks Bidso-supplied lines at the job worker; Zoho DC
                  is still created as &ldquo;Others&rdquo; (job-work isn&apos;t a Zoho-bound
                  value on this org).
                </p>
              )}
            </div>

            {/* Customer (others) OR Job-Worker Vendor (job_work) */}
            {form.challan_type === "job_work" ? (
              <div>
                <Label>Job-Worker Vendor *</Label>
                {form.vendor_id ? (
                  <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded px-3 py-2">
                    <span className="text-sm">
                      {form.vendor_name}
                      <span className="text-xs text-zinc-500 ml-2">{form.vendor_id}</span>
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, vendor_id: "", vendor_name: "" })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Select
                      value=""
                      onValueChange={(v) => {
                        const vend = jobWorkVendors.find((x) => x.id === v);
                        if (vend) setForm({ ...form, vendor_id: vend.id, vendor_name: vend.name });
                      }}
                    >
                      <SelectTrigger data-testid="dc-vendor-picker">
                        <SelectValue placeholder={jwVendorsLoading ? "Loading…" : "Select a job-worker vendor"} />
                      </SelectTrigger>
                      <SelectContent>
                        {jobWorkVendors.length === 0 && !jwVendorsLoading && (
                          <div className="px-3 py-2 text-xs text-zinc-500">
                            No is_job_worker vendors found. Flag one in Vendor Management first.
                          </div>
                        )}
                        {jobWorkVendors.map((v) => (
                          <SelectItem key={v.id} value={v.id} data-testid={`dc-vendor-opt-${v.vendor_id || v.id}`}>
                            {v.name}
                            {!v.zoho_customer_id && (
                              <span className="text-[10px] text-rose-600 ml-2">(no zoho_customer_id — will 400)</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Only vendors flagged <code>is_job_worker=true</code>. The Zoho DC push uses the
                      vendor&apos;s <code>zoho_customer_id</code>.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div>
                <Label>Customer *</Label>
                {form.customer_id ? (
                  <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded px-3 py-2">
                    <span className="text-sm">{form.customer_name} <span className="text-xs text-zinc-500 ml-2">{form.customer_id}</span></span>
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, customer_id: "", customer_name: "" })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Search customer by name…"
                      value={custQuery}
                      onChange={(e) => setCustQuery(e.target.value)}
                      data-testid="dc-customer-search-input"
                    />
                    {searchingCust && <div className="text-xs text-zinc-500 mt-1">Searching…</div>}
                    {/* Feb 2026 — defect C: visible min-char gate hint. */}
                    {!searchingCust && custQuery.length > 0 && custQuery.length < 2 && (
                      <div className="text-xs text-zinc-500 italic mt-1" data-testid="dc-customer-search-hint">
                        Type at least 2 characters…
                      </div>
                    )}
                    {custResults.length > 0 && (
                      <div className="border rounded mt-1 max-h-48 overflow-y-auto bg-white">
                        {custResults.map((c) => (
                          <button
                            key={c.contact_id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm border-b last:border-b-0"
                            onClick={() => { setForm({ ...form, customer_id: c.contact_id, customer_name: c.contact_name }); setCustQuery(""); setCustResults([]); }}
                            data-testid={`dc-customer-result-${c.contact_id}`}
                          >
                            {c.contact_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Item type + source branch + date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Item Type *</Label>
                <Select value={form.item_type} onValueChange={(v) => { setForm({ ...form, item_type: v }); setRows([blankLine()]); }}>
                  <SelectTrigger data-testid="dc-item-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RM">Raw Material</SelectItem>
                    <SelectItem value="BUYER_SKU">Buyer SKU</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source Branch (for stock picker)</Label>
                <Select value={form.source_branch} onValueChange={(v) => setForm({ ...form, source_branch: v })}>
                  <SelectTrigger data-testid="dc-source-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="dc-date" />
              </div>
            </div>

            {/* Optional text fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Reference Number</Label>
                <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="PO / lorry no. (optional)" data-testid="dc-reference" />
              </div>
              {/* Feb 2026 — "Place of Supply" input removed. It never
                  reached Zoho as a real field (Zoho's DC API doesn't
                  accept it — only bills/invoices do), so collecting it
                  here misled users into thinking a wrong entry could
                  break Zoho pushes. */}
              <div>
                <Label>Consignee Name</Label>
                <Input value={form.consignee_name} onChange={(e) => setForm({ ...form, consignee_name: e.target.value })} placeholder="(optional)" data-testid="dc-consignee-name" />
              </div>
              <div>
                <Label>Delivery Method</Label>
                <Input value={form.delivery_method} onChange={(e) => setForm({ ...form, delivery_method: e.target.value })} placeholder="e.g., By road (optional)" data-testid="dc-delivery-method" />
              </div>
              <div className="md:col-span-2">
                <Label>Consignee Address</Label>
                <Textarea value={form.consignee_address} onChange={(e) => setForm({ ...form, consignee_address: e.target.value })} rows={2} placeholder="(optional)" data-testid="dc-consignee-address" />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="(optional)" data-testid="dc-notes" />
              </div>
            </div>

            {/* Line items */}
            <div className="border rounded-lg p-3 bg-zinc-50/50">
              <div className="flex justify-between items-center mb-2">
                <Label>Line Items *</Label>
                <Button size="sm" variant="outline" onClick={addRow} disabled={!form.source_branch} title={!form.source_branch ? "Pick a source branch first" : ""} data-testid="dc-add-row">
                  <Plus className="w-3 h-3 mr-1" />Add Row
                </Button>
              </div>
              <div className="space-y-2">
                {rows.map((r, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <Label className="text-xs uppercase tracking-wider text-zinc-500">Item</Label>
                      <StockItemSearch
                        branch={form.source_branch}
                        type={form.item_type}
                        value={r.item_id}
                        onSelect={(info) => setRow(idx, { item_id: info.item_id || "", item_name: info.name || "", item_stock: info.current_stock ?? null })}
                        disabled={!form.source_branch}
                        testId={`dc-row-item-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-500">Qty</Label>
                      <Input type="number" step="any" value={r.quantity} onChange={(e) => setRow(idx, { quantity: e.target.value })} className="no-spinner" onWheel={(e) => e.currentTarget.blur()} data-testid={`dc-row-qty-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs uppercase tracking-wider text-zinc-500">Rate</Label>
                      <Input type="number" step="any" value={r.rate} onChange={(e) => setRow(idx, { rate: e.target.value })} className="no-spinner" onWheel={(e) => e.currentTarget.blur()} data-testid={`dc-row-rate-${idx}`} />
                    </div>
                    <div className="col-span-1">
                      <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} disabled={rows.length === 1} data-testid={`dc-row-remove-${idx}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                    {form.challan_type === "job_work" && form.item_type === "RM" && (
                      <div className="col-span-12 -mt-1">
                        <label className="inline-flex items-center gap-2 text-xs text-zinc-600">
                          <span className="uppercase tracking-wider">Ownership:</span>
                          <select
                            value={r.ownership || "BIDSO_SUPPLIED"}
                            onChange={(e) => setRow(idx, { ownership: e.target.value })}
                            className="border border-zinc-300 rounded px-2 py-0.5 text-xs"
                            data-testid={`dc-row-ownership-${idx}`}
                          >
                            <option value="BIDSO_SUPPLIED">Bidso-Supplied (tracked at vendor)</option>
                            <option value="VENDOR_SUPPLIED">Vendor-Supplied (no ledger)</option>
                          </select>
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingId(null); }} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => submitCreate({ pushAfterSave: false })}
              disabled={submitting}
              data-testid="dc-submit-save-only"
              title="Save the DC as a Playfield draft. Debits stock now, no Zoho call. Push later from the DC row."
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? "Save Draft" : "Save Draft (Playfield only)"}
            </Button>
            <Button
              onClick={() => submitCreate({ pushAfterSave: true })}
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="dc-submit"
              title="Save the DC and immediately push it to Zoho Books"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? "Save & Push to Zoho" : "Save & Push to Zoho"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== IBT DC creation dialog ===================== */}
      <Dialog open={showIbtDC} onOpenChange={setShowIbtDC}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dc-ibt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {ibtDcMode === "create"
                ? `Create Delivery Challan — ${ibtDcTransfer?.transfer_code}`
                : ibtDcMode === "edit"
                ? `Edit DC — ${ibtDcTransfer?.zoho_dc_number || ibtDcTransfer?.transfer_code}`
                : `DC — ${ibtDcTransfer?.zoho_dc_number || ibtDcTransfer?.transfer_code}`}
            </DialogTitle>
            <DialogDescription>
              {ibtDcMode === "create"
                ? "Creates a draft DC in Zoho Books linked to this IBT. Once Zoho marks the DC as Open, the IBT auto-advances to DISPATCHED."
                : ibtDcMode === "edit"
                ? "Edits push immediately to Zoho. Once Zoho marks the DC non-draft, this becomes view-only."
                : "Read-only — once a DC leaves draft status it can only be modified in Zoho."}
            </DialogDescription>
          </DialogHeader>

          {ibtDcTransfer && (
            <div className="space-y-4">
              {ibtDcItemBlockers?.items?.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-xs" data-testid="dc-ibt-item-blockers">
                  <div className="font-semibold text-amber-900 mb-2">
                    Items missing master data — DC cannot be created yet
                  </div>
                  <ul className="space-y-1.5 text-amber-900/90">
                    {ibtDcItemBlockers.items.map((it) => (
                      <li key={it.internal_id} className="flex items-start gap-2">
                        <span className="font-mono font-bold min-w-[90px]">{it.internal_id}</span>
                        <span className="flex-1">
                          {it.missing_fields?.length > 0
                            ? <>Missing in {it.found_kind} master: <span className="font-mono">{it.missing_fields.join(", ")}</span></>
                            : it.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 bg-zinc-50 p-3 rounded text-sm">
                <div>
                  <Label className="text-xs text-zinc-500">Transfer Code</Label>
                  <div className="font-mono font-bold text-blue-600">{ibtDcTransfer.transfer_code}</div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Source → Destination</Label>
                  <div>{ibtDcTransfer.source_branch} → {ibtDcTransfer.destination_branch}</div>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Items</Label>
                  <div className="text-xs">
                    {ibtDcTransfer.items?.length > 0
                      ? ibtDcTransfer.items.map((i) => `${i.item_id} × ${i.quantity}`).join(", ")
                      : `${ibtDcTransfer.item_id} × ${ibtDcTransfer.quantity}`}
                  </div>
                </div>
              </div>

              <div>
                <Label>Customer <span className="text-red-500">*</span></Label>
                <Input
                  value={ibtCustQuery}
                  onChange={(e) => { setIbtCustQuery(e.target.value); setIbtDcForm({ ...ibtDcForm, customer_id: "", customer_name: "" }); }}
                  placeholder="Type at least 2 characters to search Zoho customers…"
                  data-testid="dc-ibt-customer-search"
                  disabled={ibtDcMode === "view"}
                />
                {ibtDcForm.customer_id && (
                  <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Selected: <span className="font-medium">{ibtDcForm.customer_name}</span>
                  </div>
                )}
                {/* Feb 2026 — defect C: visible min-char gate hint. */}
                {!ibtDcForm.customer_id && ibtCustQuery.length > 0 && ibtCustQuery.length < 2 && (
                  <div className="text-xs text-zinc-500 italic mt-1" data-testid="dc-ibt-customer-search-hint">
                    Type at least 2 characters…
                  </div>
                )}
                {ibtCustQuery.length >= 2 && !ibtDcForm.customer_id && (
                  <div className="border border-zinc-200 rounded mt-1 max-h-48 overflow-y-auto bg-white">
                    {ibtCustSearching && (
                      <div className="p-2 text-xs text-zinc-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                      </div>
                    )}
                    {!ibtCustSearching && ibtCustResults.length === 0 && (
                      <div className="p-2 text-xs text-zinc-400">No matches</div>
                    )}
                    {ibtCustResults.map((c) => (
                      <button
                        key={c.contact_id}
                        type="button"
                        className="w-full text-left p-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                        data-testid={`dc-ibt-customer-option-${c.contact_id}`}
                        onClick={() => {
                          setIbtDcForm({ ...ibtDcForm, customer_id: c.contact_id, customer_name: c.contact_name });
                          setIbtCustQuery(c.contact_name);
                          setIbtCustResults([]);
                        }}
                      >
                        <div className="font-medium">{c.contact_name}</div>
                        {(c.company_name || c.gst_no) && (
                          <div className="text-xs text-zinc-500">
                            {c.company_name}{c.company_name && c.gst_no && " · "}{c.gst_no}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Challan Type</Label>
                  <Select value={ibtDcForm.challan_type} onValueChange={(v) => setIbtDcForm({ ...ibtDcForm, challan_type: v })} disabled={ibtDcMode === "view"}>
                    <SelectTrigger data-testid="dc-ibt-challan-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="others">Others (recommended default)</SelectItem>
                      {/* "Job Work" removed Feb 2026 — it was rejected by
                          Zoho with an opaque error on this org, and job-work
                          challans aren't a legitimate IBT concept anyway
                          (IBTs are Bidso-to-Bidso inter-branch, not vendor
                          job work). Use the Standalone DC path for job work. */}
                      <SelectItem value="goods_sent_on_approval">Goods Sent on Approval</SelectItem>
                      <SelectItem value="skip_outward_registration">Skip Outward Registration</SelectItem>
                      <SelectItem value="supply_of_liquid_gas">Supply of Liquid Gas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Challan Date</Label>
                  <Input type="date" value={ibtDcForm.date} onChange={(e) => setIbtDcForm({ ...ibtDcForm, date: e.target.value })} data-testid="dc-ibt-date" disabled={ibtDcMode === "view"} />
                </div>
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={ibtDcForm.reference_number}
                  onChange={(e) => setIbtDcForm({ ...ibtDcForm, reference_number: e.target.value })}
                  placeholder="Defaults to IBT transfer code"
                  data-testid="dc-ibt-reference"
                  disabled={ibtDcMode === "view"}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={ibtDcForm.notes}
                  onChange={(e) => setIbtDcForm({ ...ibtDcForm, notes: e.target.value })}
                  rows={2}
                  placeholder="If left blank, IBT notes + vehicle/driver info will be used."
                  data-testid="dc-ibt-notes"
                  disabled={ibtDcMode === "view"}
                />
              </div>

              <div>
                <Label>Terms &amp; Conditions</Label>
                <Textarea
                  value={ibtDcForm.terms_and_conditions}
                  onChange={(e) => setIbtDcForm({ ...ibtDcForm, terms_and_conditions: e.target.value })}
                  rows={2}
                  data-testid="dc-ibt-terms"
                  disabled={ibtDcMode === "view"}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIbtDC(false)} disabled={ibtDcSubmitting}>
              {ibtDcMode === "view" ? "Close" : "Cancel"}
            </Button>
            {ibtDcMode !== "view" && (
              <Button
                onClick={submitIbtDC}
                disabled={ibtDcSubmitting || !ibtDcForm.customer_id}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="dc-ibt-submit"
              >
                {ibtDcSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {ibtDcMode === "edit" ? "Save & Sync to Zoho" : "Create Draft DC in Zoho"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================= Standalone Detail ====================== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl" data-testid="dc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono">{detail?.dc_code}</span>
              {detail?.zoho_dc_status && (
                <Badge className={`${DC_STATUS_COLORS[detail.zoho_dc_status] || ""} border`}>
                  {detail.zoho_dc_status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {detail?.customer_name} — Zoho #{detail?.zoho_dc_number || "(not synced)"}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-zinc-500">Date:</span> {detail.date || "—"}</div>
                <div><span className="text-zinc-500">Ref #:</span> {detail.reference_number || "—"}</div>
                <div><span className="text-zinc-500">Delivery method:</span> {detail.delivery_method || "—"}</div>
                <div className="col-span-2"><span className="text-zinc-500">Consignee:</span> {detail.consignee_name || "—"}</div>
                <div className="col-span-2"><span className="text-zinc-500">Address:</span> {detail.consignee_address || "—"}</div>
                <div className="col-span-2"><span className="text-zinc-500">Notes:</span> {detail.notes || "—"}</div>
              </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-wider text-zinc-500">Line items</span>
                <BookVehicleButton onClick={() => setBookVehicleOpen(true)} />
              </div>
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                    <tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th></tr>
                  </thead>
                  <tbody>
                    {(detail.line_items || []).map((li, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{li.item_id} <span className="text-zinc-500">— {li.name}</span></td>
                        <td className="px-3 py-2 text-right">{li.quantity}</td>
                        <td className="px-3 py-2 text-right">₹{li.rate}</td>
                        <td className="px-3 py-2 text-right">₹{li.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right font-semibold">Total: ₹{Number(detail.total_amount || 0).toLocaleString("en-IN")}</div>
              <BookVehicleDialog
                open={bookVehicleOpen}
                onClose={() => setBookVehicleOpen(false)}
                sourceType="DC"
                sourceId={detail?.id}
                sourceLabel={detail ? `DC ${detail.dc_code}` : ""}
              />
              {/* Push status ledger — visible for standalone DCs that
                  haven't reached a "closed in Zoho" state. Feb 2026
                  save-vs-push decoupling (spec AC7 + AC8). */}
              {(detail.zoho_push_status || detail.zoho_push_error) && (
                <div className="mt-3 pt-3 border-t border-zinc-100 text-xs text-zinc-500 space-y-1" data-testid="dc-detail-push-info">
                  <div>Push status: <span className="font-mono text-zinc-900">{detail.zoho_push_status || "—"}</span> · retries: {detail.zoho_push_retry_count || 0}</div>
                  {detail.zoho_push_error && (
                    <div className="text-rose-700 break-words">Last error: {detail.zoho_push_error}</div>
                  )}
                </div>
              )}
            </div>
          )}
          {detail && detail.source !== "IBT" && (
            <DialogFooter className="gap-2">
              {RETRY_PUSH_STATES.has(detail.zoho_dc_status) && (
                <PermButton
                  entity="Dispatch" action="UPDATE"
                  onClick={() => retryPush({
                    id: detail.id, dc_code: detail.dc_code,
                    source: "STANDALONE", zoho_dc_status: detail.zoho_dc_status,
                  })}
                  disabled={pushingIds.has(detail.id)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="dc-detail-retry-push-btn"
                >
                  {pushingIds.has(detail.id) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Push to Zoho
                </PermButton>
              )}
              {RELEASE_STATES.has(detail.zoho_dc_status) && (
                <PermButton
                  entity="Dispatch" action="DELETE"
                  variant="outline"
                  onClick={async () => {
                    await releaseDc({
                      id: detail.id, dc_code: detail.dc_code,
                      source: "STANDALONE", zoho_dc_status: detail.zoho_dc_status,
                    });
                    setShowDetail(false);
                  }}
                  disabled={deletingIds.has(detail.id)}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="dc-detail-release-btn"
                >
                  {deletingIds.has(detail.id) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Trash2 className="w-4 h-4 mr-2" />
                  Release / Delete
                </PermButton>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryChallan;
